import { createSelector, createSlice } from '@reduxjs/toolkit';
import * as R from 'ramda';
import { getStringifiedQueryParams } from 'utils/helpers/fetch';
import { createSnack } from 'snackbar/snackbarSlice';
import http from 'utils/http';

const initialState = {
  affiliateTableChange: false,
  affiliates: [],
  dialogOpen: false,
  globalCustomerData: null,
  legendDrawerOpen: false,
  mobileSidebarOpen: true,
  parentCompanies: [],
  parentCompaniesIndex: 0,
  parentCompaniesPending: true,
  permissionsPending: true,
  targetUser: null,
  targetUserPermissionsPending: true,
  userAffiliateTable: [],
  userTableChange: false,
  users: [],
};

const reducers = {
  resetSecurity: R.always(initialState),
  setAffiliateTableChange: R.assoc('affiliateTableChange'),
  setAffiliates: R.assoc('affiliates'),
  setDialogOpen: R.assoc('dialogOpen'),
  setGlobalCustomerData: R.assoc('globalCustomerData'),
  setLegendDrawerOpen: R.assoc('legendDrawerOpen'),
  setMobileSidebarOpen: R.assoc('mobileSidebarOpen'),
  setParentCompanies: R.assoc('parentCompanies'),
  setParentCompaniesIndex: R.assoc('parentCompaniesIndex'),
  setParentCompaniesPending: R.assoc('parentCompaniesPending'),
  setPermissionsPending: R.assoc('permissionsPending'),
  setTableDataPending: R.assoc('tableDataPending'),
  setTargetUser: R.assoc('targetUser'),
  setTargetUserPermissionsPending: R.assoc('targetUserPermissionsPending'),
  setUserAffiliateTable: R.assoc('userAffiliateTable'),
  setUserTableChange: R.assoc('userTableChange'),
  setUsers: R.assoc('users'),
};

const { actions, reducer } = createSlice({
  initialState,
  name: 'security',
  // eslint-disable-next-line react-hooks/rules-of-hooks
  reducers: R.map(singleReducer => R.flip(R.useWith(singleReducer, [R.prop('payload')])), reducers),
});

// actions
export const {
  resetSecurity,
  setAffiliates,
  setAffiliateTableChange,
  setDialogOpen,
  setGlobalCustomerData,
  setMobileSidebarOpen,
  setLegendDrawerOpen,
  setParentCompanies,
  setParentCompaniesIndex,
  setParentCompaniesPending,
  setPermissionsPending,
  setTargetUserPermissionsPending,
  setTargetUser,
  setUserAffiliateTable,
  setUsers,
  setUserTableChange,
} = actions;

// Selectors
const mergePath = R.concat(['security']);
export const getStoreValueByProp = R.o(R.path, mergePath);

export const getParentCompanies = getStoreValueByProp(['parentCompanies']);
export const getParentCompaniesPending = getStoreValueByProp(['parentCompaniesPending']);
export const getParentCompaniesIndex = getStoreValueByProp(['parentCompaniesIndex']);
export const getAllParentCompanies = createSelector(getParentCompanies);
export const getCurrentParentCompany = createSelector(
  getParentCompaniesIndex,
  getParentCompanies,
  R.compose(R.defaultTo({}), R.prop)
);

// default show sidebar when we have pending state or list with more than 2 options
export const getCanShowSidebar = R.either(
  getParentCompaniesPending,
  R.compose(R.gte(R.__, 2), R.length, getParentCompanies)
);

export const updateUserPermission = user => dispatch => {
  dispatch(setTargetUserPermissionsPending(true));
  http
    .post('user-profiles/set-user-permissions', user)
    .then(({ data }) => {
      dispatch(
        createSnack({
          message: `Your changes to user permissions have been saved for ${data.userId}`,
          options: {
            persist: false,
          },
        })
      );
    })
    .catch(error => {
      dispatch(
        createSnack({
          message:
            error.response?.data?.detail ||
            'Due to an error, users permissions could not be updated.',
          options: {
            variant: 'error',
          },
        })
      );
    })
    .finally(() => {
      dispatch(setTargetUserPermissionsPending(false));
    });
};

export const fetchParentCompanies = () => dispatch => {
  const url = 'user-profiles/get-user-affiliate-global-customers';
  http.get(url).then(({ data }) => {
    dispatch(setParentCompanies(data));
    dispatch(setParentCompaniesPending(false));
  });
};

export const getGlobalCustomerMembers =
  ({ customerId }) =>
  dispatch => {
    dispatch(setPermissionsPending(false));
    const params = getStringifiedQueryParams({ customerId });
    const url = `user-profiles/get-global-customer-members?${params}`;
    http.get(url).then(({ data: { affiliates, members } }) => {
      dispatch(setAffiliates(affiliates));
      dispatch(setUsers(members));
      dispatch(setUserAffiliateTable(members));
    });
  };

export const fetchPermissionsForUser =
  ({ customerId, userId }, cancelToken) =>
  dispatch => {
    dispatch(setTargetUserPermissionsPending(true));
    dispatch(setTargetUser(null));
    const params = getStringifiedQueryParams({ customerId, userId });
    const url = `user-profiles/get-user-permissions?${params}`;
    http
      .get(url, { cancelToken })
      .then(({ data }) => {
        dispatch(setTargetUser(data));
      })
      .catch(error => {
        dispatch(setTargetUser({ ...error, error: 'Unable to fetch user permissions' }));
      })
      .finally(() => {
        dispatch(setTargetUserPermissionsPending(false));
      });
  };

export default reducer;
